// The saloon car interior: the Blender-built, light-baked GLB (public/assets/saloon/_src/
// build_saloon.py) placed at sim/saloon.ts SALOON, plus the live bits the bake can't hold: bulb
// glow, the leaded skylight, window glass, drifting dust in the skylight shafts, and the hot-dog
// plate. The baked lightmap carries the room's light; the realtime sun only adds a faint key so
// cowboys cast soft shadows on the boards.
//
// Draws: the room is one mesh per material (~14) + glass + skylight + bulbs + motes + stools.

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import { CAR, SALOON, STOVE } from '../../sim/saloon';
import { SpaceView } from './spaceview';
import { SaloonProps, RACK_CLEAR } from './props';
import { SaloonDressing } from './dressing';

const BASE = (import.meta as unknown as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? '/';
const ASSET = BASE + 'assets/saloon/';

/** How much of the realtime direct light the baked surfaces keep (the bake already has it). */
const DIRECT_KEEP = 0.28;
/** contrast curve on the (sRGB-decoded, 0..1) lightmap; the gain holds a 0.6 pool at its baked level */
const LM_CURVE = 2.0;
/** how far the vault darkens toward the clerestory */
const VAULT_DIM = 0.75;
/** golden haze: about a quarter of the light at the far door (~19 m) is the air's own */
const HAZE = [0.085, 0.04, 0.014];
const HAZE_K = 0.015;
/** the stained glass, drawn unlit and untonemapped: amber, below the bulbs */
const SKY_TINT = new THREE.Color(0.5, 0.34, 0.17);
/**
 * Lamp pools in the bake's response: each pendant (both rows) lifts the light under it and the boards,
 * panels and booths between lamps fall away, warmer in the pools and cooler-dark in the gaps; the two
 * end walls sit back in shadow. GLSL, run on irradiance after the vault term (car-local xz in vCarXZ).
 */
const POOL_Z = [-7.6, -5.2, -2.8, -0.4, 2.0, 4.4, 6.8];
const POOLS = `float pool = 0.0;
            vec2 pd;
            ${POOL_Z.map((z) => `pd = vCarXZ - vec2(1.38, ${z.toFixed(1)}); pool += exp(-dot(pd * pd, vec2(0.3, 1.5))); pd = vCarXZ - vec2(-1.1, ${z.toFixed(1)}); pool += exp(-dot(pd * pd, vec2(0.3, 1.5)));`).join('\n            ')}
            pd = vCarXZ - vec2(0.0, 9.5); pool += 0.75 * exp(-dot(pd * pd, vec2(0.18, 0.6))); // the end doors' sconces
            pd = vCarXZ - vec2(0.0, -9.5); pool += 0.75 * exp(-dot(pd * pd, vec2(0.18, 0.6)));
            pd = vCarXZ - vec2(${(STOVE.x + 0.3).toFixed(2)}, ${STOVE.z.toFixed(2)}); pool += 0.6 * exp(-dot(pd, pd) * 1.2); // the stove's grate
            pool = clamp(pool, 0.0, 1.0);
            float endK = smoothstep(7.6, 9.5, abs(vCarXZ.y));
            float ends = mix(1.0, vCarXZ.y < 0.0 ? 0.62 : 0.9, endK);
            irradiance *= mix(vec3(0.34, 0.31, 0.32), vec3(1.3, 1.17, 1.0), pool) * ends * mix(vec3(1.0), vec3(1.08, 0.86, 0.66), endK); // the ends warm, not grey
            float poolSpec = mix(0.25, 1.0, pool) * ends * (1.0 - 0.35 * endK); // the grey room env's sheen falls off between lamps too`;
const LM_CURVE_GAIN = 1.67; // 0.6^(1 - LM_CURVE)
/** _src/lightmap.py prints LM_SCALE (the bake's 99.7th percentile); the JPG holds lightmap / LM_SCALE. */
const LM_SCALE = 1.197;
/** three applies lightMap * intensity / PI to the albedo; PI matches the bake, x1.6 matches Blender's view exposure (0.6 stops) */
const LM_GAIN = Math.PI * 1.6;

export class SaloonCar {
  readonly group = new THREE.Group();
  /** the roof pieces the launch blows out (the skylight glass + its frame) */
  skylight: THREE.Object3D | null = null;
  ready: Promise<void>;
  private motes: THREE.Points;
  private moteMat: THREE.ShaderMaterial;
  private bulbMat: THREE.MeshBasicMaterial | null = null;
  private skyMat: THREE.MeshBasicMaterial | null = null;
  private flicker = 1;
  /** the exterior shell: hidden indoors so the windows look out on the space view */
  private hullMats: THREE.Material[] = [];
  private indoor = true;
  readonly space: SpaceView;
  readonly props: SaloonProps;

  constructor(private renderer: THREE.WebGLRenderer, quality: number) {
    this.group.name = 'saloon';
    this.group.position.set(SALOON.x, SALOON.y, SALOON.z);

    // dust in the skylight shafts
    const n = quality >= 2 ? 420 : 200;
    const pos = new Float32Array(n * 3);
    const seed = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      pos[i * 3] = (Math.random() - 0.5) * CAR.skyHW * 2.6;
      pos[i * 3 + 1] = 0.3 + Math.random() * (CAR.skyH - 0.4);
      pos[i * 3 + 2] = (Math.random() - 0.5) * CAR.hl * 2;
      seed[i] = Math.random();
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    g.setAttribute('seed', new THREE.BufferAttribute(seed, 1));
    this.moteMat = new THREE.ShaderMaterial({
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
      uniforms: { uTime: { value: 0 }, uScale: { value: 1 } },
      vertexShader: /* glsl */`
        attribute float seed; uniform float uTime; uniform float uScale; varying float vA;
        void main() {
          vec3 p = position;
          float t = uTime * (0.05 + seed * 0.06) + seed * 40.0;
          p.x += sin(t * 1.3) * 0.25; p.y += sin(t * 0.7 + seed * 9.0) * 0.35; p.z += cos(t) * 0.3;
          vec4 mv = modelViewMatrix * vec4(p, 1.0);
          gl_Position = projectionMatrix * mv;
          gl_PointSize = uScale * (1.0 + seed * 1.5) * 6.0 / -mv.z;
          // brightest in the shaft under the skylight
          vA = (0.25 + 0.75 * smoothstep(1.4, 0.3, abs(p.x))) * smoothstep(0.0, 0.8, p.y) * (0.35 + 0.65 * fract(seed * 7.13));
        }`,
      fragmentShader: /* glsl */`
        varying float vA;
        void main() { float d = length(gl_PointCoord - 0.5); if (d > 0.5) discard; gl_FragColor = vec4(vec3(1.0, 0.82, 0.55) * vA * 0.5 * (1.0 - d * 2.0), 1.0); }`,
    });
    this.motes = new THREE.Points(g, this.moteMat);
    this.motes.frustumCulled = false;
    this.group.add(this.motes);

    this.space = new SpaceView(quality);
    this.group.add(this.space.group);
    this.props = new SaloonProps();
    this.group.add(this.props.group);
    const dressing = new SaloonDressing();
    this.group.add(dressing.group);
    if (quality > 0) { this.group.add(lampPools()); this.group.add(skyShafts()); }

    this.ready = Promise.all([this.load(quality), dressing.ready]).then(() => undefined);
  }

  private async load(quality: number) {
    const tl = new THREE.TextureLoader();
    const [gltf, lm] = await Promise.all([
      new GLTFLoader().setMeshoptDecoder(MeshoptDecoder).loadAsync(ASSET + 'saloon.glb'),
      tl.loadAsync(ASSET + 'saloon_lm.jpg'),
    ]);
    lm.channel = 1;
    lm.flipY = false;
    lm.colorSpace = THREE.SRGBColorSpace;
    const pmrem = new THREE.PMREMGenerator(this.renderer);
    const env = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    pmrem.dispose();

    const root = gltf.scene;
    root.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      const mats = Array.isArray(m.material) ? m.material : [m.material];
      const name = m.name.toLowerCase();
      if (name.startsWith('windows')) {
        // clear glass with a faint reflection of the room: the space view is outside it
        m.material = new THREE.MeshPhysicalMaterial({
          color: 0xd8c8a8, transparent: true, opacity: 0.05, roughness: 0.04, metalness: 0, envMap: env, envMapIntensity: 0.12, depthWrite: false,
        });
        m.renderOrder = 2;
        return;
      }
      if (name.startsWith('skylight')) {
        const src = mats[0] as THREE.MeshStandardMaterial;
        this.skyMat = new THREE.MeshBasicMaterial({ map: src.map ?? null, color: SKY_TINT.clone(), toneMapped: false }); // amber and dimmer than the lamps: the pendants lead
        m.material = this.skyMat;
        this.skylight = m;
        return;
      }
      if (name.startsWith('bulbs')) {
        this.bulbMat = new THREE.MeshBasicMaterial({ color: new THREE.Color(2.6, 1.3, 0.45) }); // amber filaments, not white: the bloom gives them their halo
        m.material = this.bulbMat;
        return;
      }
      // everything else is baked
      for (const mm of mats) if (/^(hull|hull_band|copper)$/.test(mm.name) && !this.hullMats.includes(mm)) this.hullMats.push(mm);
      m.receiveShadow = true;
      for (const mm of mats) {
        const s = mm as THREE.MeshStandardMaterial;
        s.lightMap = lm;
        s.lightMapIntensity = LM_SCALE * LM_GAIN;
        s.envMap = env;
        s.envMapIntensity = s.metalness > 0.5 ? 0.55 : 0.5; // varnish sheen (specular only: the diffuse IBL is cut below)
        if (quality < 2) { s.normalMap = null; }
        s.onBeforeCompile = (sh) => {
          // the bake holds the room's light: cut the scene's ambient/hemi and the env map's flat diffuse
          // (it washed out the bake's pools); cut ambient BEFORE the lightmap is added
          // to irradiance (cutting it after would dim the lightmap too), and most of the realtime sun
          // steepen the bake's response: the normalised lightmap's mids pushed down (pools stay bright,
          // the ceiling and the gaps between lamps fall away), LM_CURVE_GAIN keeps the pools' level
          const maps = THREE.ShaderChunk.lights_fragment_maps.replace('vec3 lightMapIrradiance = lightMapTexel.rgb * lightMapIntensity;',
            `vec3 lightMapIrradiance = pow(lightMapTexel.rgb, vec3(${LM_CURVE.toFixed(2)})) * ${LM_CURVE_GAIN.toFixed(2)} * lightMapIntensity;`);
          // the vault falls away above the cornice (the ref's lamps glow against a near-black ceiling)
          sh.vertexShader = sh.vertexShader.replace('#include <common>', '#include <common>\nvarying float vCarY;\nvarying vec2 vCarXZ;')
            .replace('#include <project_vertex>', `#include <project_vertex>\nvec4 carW = modelMatrix * vec4(transformed, 1.0); vCarY = carW.y; vCarXZ = carW.xz - vec2(${SALOON.x.toFixed(2)}, ${SALOON.z.toFixed(2)});`);
          sh.fragmentShader = sh.fragmentShader.replace('#include <common>', `#include <common>\nvarying float vCarY;\nvarying vec2 vCarXZ;`);
          // warm air: the far end of the car sits in a golden haze (linear HDR; the post pass encodes)
          sh.fragmentShader = sh.fragmentShader.replace('#include <dithering_fragment>',
            `#include <dithering_fragment>\n gl_FragColor.rgb = mix(gl_FragColor.rgb, vec3(${HAZE.map((v) => v.toFixed(3)).join(', ')}), 1.0 - exp(-length(vViewPosition) * ${HAZE_K.toFixed(4)}));`);
          sh.fragmentShader = sh.fragmentShader.replace('#include <lights_fragment_maps>',
            `irradiance *= 0.15;\n${maps}\n iblIrradiance *= 0.08; reflectedLight.directDiffuse *= ${DIRECT_KEEP.toFixed(2)}; reflectedLight.directSpecular *= 0.5;
            float vaultK = ${VAULT_DIM.toFixed(2)} * smoothstep(${(SALOON.y + 2.3).toFixed(2)}, ${(SALOON.y + 3.6).toFixed(2)}, vCarY);
            irradiance *= mix(vec3(1.0), vec3(0.55, 0.36, 0.22), vaultK / ${VAULT_DIM.toFixed(2)}) * (1.0 - vaultK * 0.55);
            ${POOLS}
            reflectedLight.directSpecular *= 1.0 - vaultK;
            reflectedLight.directSpecular *= poolSpec;
            #if defined( RE_IndirectSpecular )
            radiance *= (1.0 - vaultK) * poolSpec;
            #endif`);
        };
        s.needsUpdate = true;
      }
    });
    clearRack(root);
    this.group.add(root);
    this.setIndoor(this.indoor); // the hull materials only exist now
  }

  /** Indoors the hull comes off and the window view of space goes up; outdoors (the launch's exterior
   *  shot) the car is a car again. */
  setIndoor(on: boolean) {
    this.indoor = on;
    for (const m of this.hullMats) m.visible = !on;
    this.space.group.visible = on;
  }

  /** The launch: hide the skylight once the posse is through it. */
  breach(on: boolean) { if (this.skylight) this.skylight.visible = !on; }

  update(dt: number, time: number, rumble: number, cam?: THREE.Camera) {
    this.moteMat.uniforms.uTime.value = time;
    this.space.update(dt, time, cam);
    this.props.update(dt);
    this.moteMat.uniforms.uScale.value = window.innerHeight / 900;
    // bulbs breathe, and stutter when the car rumbles
    const target = 1 + Math.sin(time * 3.1) * 0.02 + (rumble > 0 ? (Math.random() - 0.5) * rumble * 0.9 : 0);
    this.flicker += (target - this.flicker) * Math.min(1, dt * 20);
    if (this.bulbMat) this.bulbMat.color.setRGB(2.6 * this.flicker, 1.3 * this.flicker, 0.45 * this.flicker);
    if (this.skyMat) { const k = 1 + rumble * 0.6; this.skyMat.color.copy(SKY_TINT).multiplyScalar(k); }
    void dt;
  }

  set visible(v: boolean) { this.group.visible = v; }
}

/** Soft warm pools where the pendants throw their cones: on the bar top under the bar-side lamps and on
 * the aisle boards under the booth-side ones. The bake carries the light; these give the pool rhythm
 * the eye reads at play distance. One shared texture, additive, a dozen quads. */
function lampPools() {
  const c = document.createElement('canvas'); c.width = c.height = 128;
  const x = c.getContext('2d')!;
  const gr = x.createRadialGradient(64, 64, 0, 64, 64, 64);
  gr.addColorStop(0, 'rgba(255,190,120,1)'); gr.addColorStop(0.45, 'rgba(255,170,95,0.45)'); gr.addColorStop(1, 'rgba(255,150,80,0)');
  x.fillStyle = gr; x.fillRect(0, 0, 128, 128);
  const tex = new THREE.CanvasTexture(c); tex.colorSpace = THREE.SRGBColorSpace;
  const mat = new THREE.MeshBasicMaterial({ map: tex, color: new THREE.Color(0.85, 0.6, 0.38), transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -2 });
  const g = new THREE.Group(); g.name = 'lampPools';
  const quad = new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
  const add = (px: number, py: number, pz: number, sx: number, sz: number, k: number) => {
    const m = new THREE.Mesh(quad, k === 1 ? mat : mat.clone());
    if (k !== 1) (m.material as THREE.MeshBasicMaterial).color.multiplyScalar(k);
    m.position.set(px, py, pz); m.scale.set(sx, 1, sz); m.renderOrder = 1; g.add(m);
  };
  for (const z of [-7.6, -5.2, -2.8, -0.4, 2.0, 4.4, 6.8]) {
    if (z > CAR.barZ0 + 0.3 && z < CAR.barZ1 - 0.3) add((CAR.barX0 + CAR.barX1) / 2, CAR.barTop + 0.004, z, 0.9, 1.2, 2.0);
    add(-1.1, 0.004, z, 1.3, 1.35, 0.95);
  }
  // the aft end wall's sconce wash over the doors and the trophy
  { const m = new THREE.Mesh(new THREE.PlaneGeometry(4.6, 3.4), mat.clone()); (m.material as THREE.MeshBasicMaterial).color.multiplyScalar(0.22);
    m.position.set(0, 2.5, CAR.hl - 0.07); m.rotation.y = Math.PI; m.renderOrder = 1; g.add(m);
    const f = new THREE.Mesh(m.geometry, mat.clone()); (f.material as THREE.MeshBasicMaterial).color.multiplyScalar(0.14); f.position.set(0, 2.1, -CAR.hl + 0.07); g.add(f); }
  // the stove's grate glow on the boards around it
  add(STOVE.x + 0.85, 0.005, STOVE.z, 1.2, 1.5, 1.6); // off the hearth plate, which it lit like a lamp
  // booth tables catch the nearer lamp's spill
  for (const z of [-6.6, -3.2, 0.2, 3.6]) add(-2.3, 0.762, z, 1.0, 0.9, 0.6);
  return g;
}

/** A few soft beams slanting down from the stained-glass skylight, so the dust drifts through light. */
function skyShafts() {
  const c = document.createElement('canvas'); c.width = 32; c.height = 128;
  const x = c.getContext('2d')!;
  const v = x.createLinearGradient(0, 0, 0, 128);
  v.addColorStop(0, 'rgba(255,200,130,0.9)'); v.addColorStop(0.6, 'rgba(255,180,110,0.35)'); v.addColorStop(1, 'rgba(255,170,100,0)');
  x.fillStyle = v; x.fillRect(0, 0, 32, 128);
  const h = x.createLinearGradient(0, 0, 32, 0);
  h.addColorStop(0, 'rgba(0,0,0,1)'); h.addColorStop(0.5, 'rgba(0,0,0,0)'); h.addColorStop(1, 'rgba(0,0,0,1)');
  x.globalCompositeOperation = 'destination-out'; x.fillStyle = h; x.fillRect(0, 0, 32, 128);
  const tex = new THREE.CanvasTexture(c); tex.colorSpace = THREE.SRGBColorSpace;
  // fade each card as it turns edge-on, so the beams never read as flat sheets while the camera orbits
  const mat = new THREE.ShaderMaterial({
    uniforms: { map: { value: tex }, tint: { value: new THREE.Color(0.16, 0.11, 0.07) } },
    vertexShader: `varying vec2 vUv; varying float vFace;
      void main() { vUv = uv; vec4 mv = modelViewMatrix * vec4(position, 1.0);
        vec3 n = normalize(normalMatrix * normal); vFace = abs(dot(n, normalize(-mv.xyz)));
        gl_Position = projectionMatrix * mv; }`,
    fragmentShader: `uniform sampler2D map; uniform vec3 tint; varying vec2 vUv; varying float vFace;
      void main() { vec4 t = texture2D(map, vUv); gl_FragColor = vec4(tint * t.rgb * t.a * smoothstep(0.15, 0.6, vFace), 1.0); }`,
    transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide,
  });
  const g = new THREE.Group(); g.name = 'skyShafts';
  const plane = new THREE.PlaneGeometry(1.1, 3.4).translate(0, -1.7, 0);
  for (const z of [-2.2, 1.6, 5.4]) { // none at the fore end: it cut across the end wall
    for (const ry of [0, Math.PI / 2]) {
      const m = new THREE.Mesh(plane, mat);
      m.position.set(0, 4.05, z); m.rotation.set(0, ry, 0); m.rotateX(0.22); m.renderOrder = 2;
      g.add(m);
    }
  }
  return g;
}

/**
 * The gun case is dressed live (props.ts rackCase: back board, downlights, pegs), so the baked bits
 * inside it come out: the pilaster sconce and its bulb that shone in your face from the middle of the
 * case, the pistol pegs and the retaining rail. Every triangle lying wholly inside the RACK_CLEAR boxes is
 * folded to a point; anything that reaches outside them (the back panel, the stiles) is left alone.
 */
function clearRack(root: THREE.Object3D) {
  root.updateMatrixWorld(true);
  const v = new THREE.Vector3(), inv = new THREE.Matrix4();
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh) return;
    const pos = m.geometry.getAttribute('position') as THREE.BufferAttribute;
    if (!pos) return;
    inv.copy(m.matrixWorld).invert();
    const n = pos.count;
    const inside = new Uint8Array(n);
    let any = false;
    for (let i = 0; i < n; i++) {
      v.fromBufferAttribute(pos, i).applyMatrix4(m.matrixWorld);
      if (RACK_CLEAR.some((b) => b.containsPoint(v))) { inside[i] = 1; any = true; }
    }
    if (!any) return;
    const idx = m.geometry.getIndex();
    const tri = idx ? idx.count / 3 : n / 3;
    const at = (k: number) => (idx ? idx.getX(k) : k);
    const keep = new Uint8Array(n);
    for (let t = 0; t < tri; t++) {
      const a = at(t * 3), b = at(t * 3 + 1), c = at(t * 3 + 2);
      if (!(inside[a] && inside[b] && inside[c])) { keep[a] = keep[b] = keep[c] = 1; }
    }
    // fold onto a point at the back of the case (behind the back board)
    const c = RACK_CLEAR[0];
    const sink = new THREE.Vector3(c.max.x, c.min.y, (c.min.z + c.max.z) / 2).applyMatrix4(inv);
    let moved = 0;
    for (let i = 0; i < n; i++) if (inside[i] && !keep[i]) { pos.setXYZ(i, sink.x, sink.y, sink.z); moved++; }
    if (moved) { pos.needsUpdate = true; m.geometry.computeBoundingSphere(); }
  });
}
